import { useEffect, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Button, Input } from '@artstorefronts/ui';
import AdminLayout from '../../components/AdminLayout';

const GET_REFERRAL_SETTINGS = gql`
  query AdminReferralSettings {
    referralSettings {
      activeMechanism
      referralPoints
      giftTermMonths
      giftAllowance
    }
  }
`;

const UPDATE_REFERRAL_SETTINGS = gql`
  mutation UpdateReferralSettings($input: UpdateReferralSettingsInput!) {
    updateReferralSettings(input: $input) {
      activeMechanism
      referralPoints
      giftTermMonths
      giftAllowance
    }
  }
`;

type Mechanism = 'points' | 'subscription' | 'points_and_subscription';

interface ReferralSettings {
  activeMechanism: Mechanism;
  referralPoints: number;
  giftTermMonths: number;
  giftAllowance: number;
}

const MECHANISMS: Array<{ value: Mechanism; label: string; blurb: string }> = [
  { value: 'points', label: 'Points only', blurb: 'The friend gets the points gift; the referrer gets the points.' },
  {
    value: 'subscription',
    label: 'Subscription gift only',
    blurb: 'The friend gets a comped Human Made Art subscription; the referrer still gets the points.',
  },
  {
    value: 'points_and_subscription',
    label: 'Points + subscription',
    blurb: 'The friend gets both the points gift and the comped subscription; the referrer gets the points.',
  },
];

const TERMS = [1, 3, 6, 12];

function termLabel(months: number): string {
  if (months === 12) return '1 year';
  if (months === 1) return '1 month';
  return `${months} months`;
}

function ReferralsPage() {
  const { data, loading } = useQuery<{ referralSettings: ReferralSettings }>(GET_REFERRAL_SETTINGS, {
    fetchPolicy: 'cache-and-network',
  });
  const [updateReferralSettings, { loading: saving }] = useMutation(UPDATE_REFERRAL_SETTINGS);

  const [mechanism, setMechanism] = useState<Mechanism>('points');
  const [points, setPoints] = useState<string>('500');
  const [termMonths, setTermMonths] = useState<number>(1);
  const [allowance, setAllowance] = useState<string>('3');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Seed the form once the live settings arrive. `data` changing after a save
  // re-syncs the fields to what actually persisted.
  useEffect(() => {
    const settings = data?.referralSettings;
    if (!settings) return;
    // GraphQL serializes ReferralMechanism UPPERCASE (POINTS); this form works in the
    // lowercase internal value, so normalize on the way in and re-uppercase on save.
    setMechanism(String(settings.activeMechanism).toLowerCase() as Mechanism);
    setPoints(String(settings.referralPoints));
    setTermMonths(settings.giftTermMonths);
    setAllowance(String(settings.giftAllowance));
  }, [data?.referralSettings]);

  const showTerm = mechanism === 'subscription' || mechanism === 'points_and_subscription';

  const handleSave = async () => {
    setError(null);
    setSaved(false);
    const parsedPoints = Number(points);
    if (!Number.isInteger(parsedPoints) || parsedPoints < 0) {
      setError('Referral points must be a whole number of 0 or more.');
      return;
    }
    const parsedAllowance = Number(allowance);
    if (!Number.isInteger(parsedAllowance) || parsedAllowance < 0) {
      setError('Gift allowance must be a whole number of 0 or more.');
      return;
    }
    try {
      await updateReferralSettings({
        variables: {
          input: {
            activeMechanism: mechanism.toUpperCase(),
            referralPoints: parsedPoints,
            giftTermMonths: termMonths,
            giftAllowance: parsedAllowance,
          },
        },
      });
      setSaved(true);
    } catch (err: any) {
      setError(err?.message || 'Could not save.');
    }
  };

  return (
    <AdminLayout
      title="Referrals"
      description="Choose the one referral mechanism that is live on the site. The referrer always earns the points amount; the mechanism controls what the referred friend receives."
    >
      {loading && !data ? (
        <div>Loading…</div>
      ) : (
        <div className="max-w-2xl space-y-8">
          <section>
            <h2 className="text-lg font-semibold mb-3">Active mechanism</h2>
            <div className="space-y-2">
              {MECHANISMS.map((option) => (
                <label
                  key={option.value}
                  className="flex items-start gap-3 p-3 bg-white rounded-lg shadow cursor-pointer"
                >
                  <input
                    type="radio"
                    name="mechanism"
                    className="mt-1"
                    checked={mechanism === option.value}
                    onChange={() => setMechanism(option.value)}
                  />
                  <span>
                    <span className="font-medium block">{option.label}</span>
                    <span className="text-sm text-gray-500">{option.blurb}</span>
                  </span>
                </label>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">Referral points</h2>
            <p className="text-sm text-gray-500 mb-2">
              Awarded to the referrer on every successful referral, and to the friend when the mechanism includes
              points.
            </p>
            <Input
              type="number"
              min={0}
              step={1}
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              className="w-40"
            />
          </section>

          {showTerm && (
            <section>
              <h2 className="text-lg font-semibold mb-2">Gift length</h2>
              <p className="text-sm text-gray-500 mb-2">
                How long the comped Human Made Art subscription lasts. The friend still verifies normally.
              </p>
              <div className="flex flex-wrap gap-2">
                {TERMS.map((months) => (
                  <button
                    key={months}
                    type="button"
                    onClick={() => setTermMonths(months)}
                    className={`rounded-full border px-4 py-2 text-sm font-medium ${
                      termMonths === months
                        ? 'border-gray-900 bg-gray-900 text-white'
                        : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    {termLabel(months)}
                  </button>
                ))}
              </div>

              <div className="mt-6">
                <h3 className="text-base font-semibold mb-1">Gifts per artist</h3>
                <p className="text-sm text-gray-500 mb-2">
                  How many subscription gifts each artist can give by default. Keeping it low is what makes the gift
                  feel exclusive. Specific artists can be granted more from their user page.
                </p>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={allowance}
                  onChange={(e) => setAllowance(e.target.value)}
                  className="w-40"
                />
              </div>
            </section>
          )}

          <div className="flex items-center gap-3">
            <Button type="button" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            {saved && <span className="text-sm text-green-600">Saved.</span>}
            {error && <span className="text-sm text-red-600">{error}</span>}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ReferralsPage;
